import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePublicDecks } from '../../hooks/usePublicDecks';
import { DeckListItem } from '../../components/decks/DeckListItem';
import { TrendingUp, Clock, Library, Info, Star, Users, Shield, ChevronLeft, ChevronRight, Search, X, Filter, RefreshCw, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { Helmet } from 'react-helmet-async';
import { PublicDecksSkeleton } from '../../components/skeletons/PublicDecksSkeleton';
import { supabase } from '../../lib/supabase';
import { PokemonCard } from '../../types/pokemon';
import { getOptimizedImageUrl, IMAGE_SIZES } from '../../lib/imageUtils';
import { Link } from 'react-router-dom';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';
import { translateEnergyType } from '../../utils/energyUtils';

export function PublicDecks() {
  const { t, getPathForLang, lang } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('user_id') || undefined;
  const pageParam = searchParams.get('page');
  const sortParam = searchParams.get('sort') as 'rating' | 'recent' | 'votes' | null;
  const searchTermParam = searchParams.get('search') || '';
  const cardIdsParam = searchParams.get('cards') || '';
  
  const [sortBy, setSortBy] = useState<'rating' | 'recent' | 'votes'>(sortParam || 'rating');
  const [currentPage, setCurrentPage] = useState(pageParam ? parseInt(pageParam) : 1);
  const pageSize = 15; // Nombre de decks par page
  
  // State pour la recherche appliquée (valeur utilisée pour le filtrage réel)
  const [searchTerm, setSearchTerm] = useState(searchTermParam);
  // Nouvel état pour la valeur en cours de saisie (qui ne déclenche pas de recherche)
  const [searchInputValue, setSearchInputValue] = useState(searchTermParam);
  
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>(
    cardIdsParam ? cardIdsParam.split(',') : []
  );
  const [searchResults, setSearchResults] = useState<PokemonCard[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [cardSearchTerm, setCardSearchTerm] = useState('');
  const [selectedCards, setSelectedCards] = useState<PokemonCard[]>([]);
  
  // Nouveau state pour le mode de filtrage des cartes
  const [cardFilterMode, setCardFilterMode] = useState<'any' | 'all'>(
    searchParams.get('card_filter_mode') === 'all' ? 'all' : 'any'
  );
  // Nouveau state pour l'inclusion des variantes
  const [includeVariants, setIncludeVariants] = useState<boolean>(
    searchParams.get('include_variants') === 'true'
  );
  
  // Nouvel état pour le contrôle de l'affichage des filtres avancés
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Nouvel état pour le filtre d'énergie
  const [selectedEnergy, setSelectedEnergy] = useState<string | null>(
    searchParams.get('energy') || null
  );

  // Récupérer les types d'énergie disponibles
  const [energyTypes, setEnergyTypes] = useState<string[]>([]);
  
  // Remplacer la détection statique du mode mobile par un hook pour gérer les changements de taille d'écran
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Vérification initiale
    checkMobile();
    
    // Ajouter un écouteur pour les redimensionnements
    window.addEventListener('resize', checkMobile);
    
    // Nettoyer l'écouteur lors du démontage
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchEnergyTypes = async () => {
      try {
        const { data: distinctValues } = await supabase
          .rpc('get_distinct_card_values');
        
        if (distinctValues?.energies) {
          setEnergyTypes(distinctValues.energies.sort());
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des types d\'énergie:', error);
      }
    };

    fetchEnergyTypes();
  }, []);
  
  const { decks, totalCount, loading, error } = usePublicDecks({ 
    sortBy, 
    userId, 
    page: currentPage, 
    pageSize,
    searchTerm,
    cardIds: selectedCardIds,
    cardFilterMode,
    includeVariants,
    energy: selectedEnergy
  });

  // Charger les cartes sélectionnées au démarrage
  useEffect(() => {
    if (selectedCardIds.length > 0) {
      loadSelectedCards();
    }
  }, []);

  // Mettre à jour l'URL lorsque la page change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', currentPage.toString());
    
    // S'assurer que le paramètre de tri est toujours présent dans l'URL
    if (!params.has('sort')) {
      params.set('sort', sortBy);
    }
    
    setSearchParams(params);
  }, [currentPage, searchParams, setSearchParams, sortBy]);

  // Afficher les erreurs de chargement
  useEffect(() => {
    if (error) {
      console.error('Erreur lors du chargement des decks:', error);
    }
  }, [error]);

  // Rechercher des cartes dans la base de données
  const searchCards = async (term: string) => {
    if (term.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const { data, error: searchError } = await supabase
        .from('cards')
        .select('*')
        .ilike('card_fr', `%${term}%`)
        .limit(10);
      
      if (searchError) {
        console.error('Erreur lors de la recherche de cartes:', searchError);
        setSearchResults([]);
        return;
      }
      
      setSearchResults(data || []);
    } catch (error) {
      console.error('Erreur lors de la recherche de cartes:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Charger les données des cartes sélectionnées
  const loadSelectedCards = async () => {
    if (selectedCardIds.length === 0) return;
    
    try {
      const { data, error: loadError } = await supabase
        .from('cards')
        .select('*')
        .in('id', selectedCardIds);
      
      if (loadError) {
        console.error('Erreur lors du chargement des cartes:', loadError);
        return;
      }
      
      setSelectedCards(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des cartes:', error);
    }
  };

  // Ajouter une carte à la sélection
  const addCardToSelection = (card: PokemonCard) => {
    if (!selectedCardIds.includes(card.id.toString())) {
      const newSelectedCardIds = [...selectedCardIds, card.id.toString()];
      setSelectedCardIds(newSelectedCardIds);
      setSelectedCards([...selectedCards, card]);
      
      // Mettre à jour l'URL
      const params = new URLSearchParams(searchParams);
      params.set('cards', newSelectedCardIds.join(','));
      params.set('card_filter_mode', cardFilterMode);
      params.set('include_variants', includeVariants.toString());
      params.set('page', '1'); // Revenir à la première page
      setSearchParams(params);
      
      // Réinitialiser la page
      setCurrentPage(1);
    }
    
    // Vider les résultats de recherche
    setSearchResults([]);
    setCardSearchTerm('');
  };

  // Retirer une carte de la sélection
  const removeCardFromSelection = (cardId: string) => {
    const newSelectedCardIds = selectedCardIds.filter(id => id !== cardId);
    setSelectedCardIds(newSelectedCardIds);
    setSelectedCards(selectedCards.filter(card => card.id.toString() !== cardId));
    
    // Mettre à jour l'URL
    const params = new URLSearchParams(searchParams);
    if (newSelectedCardIds.length > 0) {
      params.set('cards', newSelectedCardIds.join(','));
    } else {
      params.delete('cards');
      // Si plus de cartes, supprimer aussi les paramètres associés
      params.delete('card_filter_mode');
      params.delete('include_variants');
    }
    params.set('page', '1'); // Revenir à la première page
    setSearchParams(params);
    
    // Réinitialiser la page
    setCurrentPage(1);
  };

  // Appliquer le filtre de recherche par texte
  const applySearchFilter = () => {
    // Mettre à jour le terme de recherche réel avec la valeur saisie
    setSearchTerm(searchInputValue.trim());
    
    // Mettre à jour l'URL
    const params = new URLSearchParams(searchParams);
    if (searchInputValue.trim()) {
      params.set('search', searchInputValue.trim());
    } else {
      params.delete('search');
    }
    params.set('page', '1'); // Revenir à la première page
    setSearchParams(params);
    
    // Réinitialiser la page
    setCurrentPage(1);
  };

  // Vider le champ de recherche
  const clearSearchInput = () => {
    setSearchInputValue('');
    // On ne modifie pas encore searchTerm pour éviter de déclencher une recherche
  };

  // Changer le mode de filtrage des cartes
  const changeCardFilterMode = (mode: 'any' | 'all') => {
    setCardFilterMode(mode);
    
    // Mettre à jour l'URL si des cartes sont sélectionnées
    if (selectedCardIds.length > 0) {
      const params = new URLSearchParams(searchParams);
      params.set('card_filter_mode', mode);
      params.set('page', '1'); // Revenir à la première page
      setSearchParams(params);
      
      // Réinitialiser la page
      setCurrentPage(1);
    }
  };
  
  // Changer l'option d'inclusion des variantes
  const toggleVariantsInclusion = () => {
    const newValue = !includeVariants;
    setIncludeVariants(newValue);
    
    // Mettre à jour l'URL si des cartes sont sélectionnées
    if (selectedCardIds.length > 0) {
      const params = new URLSearchParams(searchParams);
      params.set('include_variants', newValue.toString());
      params.set('page', '1'); // Revenir à la première page
      setSearchParams(params);
      
      // Réinitialiser la page
      setCurrentPage(1);
    }
  };

  // Effacer tous les filtres
  const clearAllFilters = () => {
    setSearchTerm('');
    setSearchInputValue('');
    setSelectedCardIds([]);
    setSelectedCards([]);
    setCardFilterMode('any');
    setIncludeVariants(false);
    setSelectedEnergy(null);
    
    // Mettre à jour l'URL
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    params.delete('cards');
    params.delete('card_filter_mode');
    params.delete('include_variants');
    params.delete('energy');
    params.set('page', '1');
    setSearchParams(params);
    
    // Réinitialiser la page
    setCurrentPage(1);
  };

  // Calculer le nombre total de pages
  const totalPages = Math.ceil((totalCount || 0) / pageSize);

  // Changer de page
  const goToPage = (page: number) => {
    // S'assurer que la page est dans les limites
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    
    // Faire défiler vers le haut de la page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Lorsqu'on change le tri, revenir à la première page
  const handleSortChange = (newSortBy: 'rating' | 'recent' | 'votes') => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    
    // Mettre à jour l'URL avec le nouveau tri et la page 1
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    params.set('sort', newSortBy);
    setSearchParams(params);
  };

  // Modification du renderPaginationButtons pour l'adapter selon le mode mobile
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtonsToShow = isMobile ? 3 : 5;
    
    // Toujours afficher la première page
    buttons.push(
      <button
        key={1}
        onClick={() => goToPage(1)}
        aria-label={`Page 1`}
        className={`relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium border ${
          currentPage === 1
            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
        }`}
      >
        1
      </button>
    );

    // Calculer la plage de pages à afficher
    let startPage = Math.max(2, currentPage - Math.floor((maxButtonsToShow - 2) / 2));
    const endPage = Math.min(startPage + maxButtonsToShow - 3, totalPages - 1);
    
    // Ajuster startPage si endPage est proche de totalPages
    if (endPage === totalPages - 1) {
      startPage = Math.max(2, endPage - (maxButtonsToShow - 3));
    }

    // Ajouter ellipse gauche si nécessaire
    if (startPage > 2) {
      buttons.push(
        <span key="left-ellipsis" className="relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm text-gray-700 bg-white border border-gray-300">
          ...
        </span>
      );
    }

    // Ajouter les pages du milieu
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          aria-label={`Page ${i}`}
          className={`relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium border ${
            currentPage === i
              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    // Ajouter ellipse droite si nécessaire
    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="right-ellipsis" className="relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm text-gray-700 bg-white border border-gray-300">
          ...
        </span>
      );
    }

    // Toujours afficher la dernière page si totalPages > 1
    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          onClick={() => goToPage(totalPages)}
          aria-label={`Page ${totalPages}`}
          className={`relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium border ${
            currentPage === totalPages
              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  if (loading) {
    return (
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('public_decks.breadcrumb'), path: '/public-decks', icon: <Library className="w-4 h-4" /> }
          ]
        }}
      >
        <PublicDecksSkeleton />
      </PageLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('public_decks.seo.title')}</title>
        <meta name="description" content={t('public_decks.seo.description')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": t('public_decks.seo.title'),
            "description": t('public_decks.seo.description'),
            "numberOfItems": totalCount || 0,
            "itemListElement": decks?.map((deck, index) => ({
              "@type": "Article",
              "position": ((currentPage - 1) * pageSize) + index + 1,
              "name": deck.name,
              "author": {
                "@type": "Person",
                "name": deck.profiles?.username
              },
              "datePublished": deck.created_at
            }))
          })}
        </script>
      </Helmet>
      
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('public_decks.breadcrumb'), path: '/public-decks', icon: <Library className="w-4 h-4" /> }
          ]
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PageTitle title={t('public_decks.page_title')} />
          
          {/* En-tête avec introduction */}
          <div className="mb-12">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {t('public_decks.header.title')}
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                {t('public_decks.header.description')}
              </p>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-3">
                  <Star className="w-8 h-8 text-yellow-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('public_decks.stats.popular_decks.title')}</h3>
                    <p className="text-gray-600 text-sm">{t('public_decks.stats.popular_decks.description')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('public_decks.stats.active_community.title')}</h3>
                    <p className="text-gray-600 text-sm">{t('public_decks.stats.active_community.description')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-green-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('public_decks.stats.standard_format.title')}</h3>
                    <p className="text-gray-600 text-sm">{t('public_decks.stats.standard_format.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Guide rapide */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('public_decks.guide.title')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('public_decks.guide.format.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('public_decks.guide.format.description')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('public_decks.guide.card_types.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('public_decks.guide.card_types.description')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('public_decks.guide.item_synergy.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('public_decks.guide.item_synergy.description')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('public_decks.guide.clear_strategy.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('public_decks.guide.clear_strategy.description')}
                </p>
              </div>
            </div>
          </div>

          {/* Annonce AdSense #1 - Après le guide du Deck Building */}
          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 mb-12 flex justify-center items-center">
            <AdSense
              adSlot="1424794127"
              className="w-full min-h-[250px]"
            />
          </div>

          {/* Filtres et tri */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {userId ? t('public_decks.explore.user_decks') : t('public_decks.explore.title')}
              </h2>
              <p className="mt-2 text-gray-600">
                {userId ? t('public_decks.explore.user_decks_description') : t('public_decks.explore.pagination').replace('{currentPage}', currentPage.toString()).replace('{totalPages}', totalPages.toString()).replace('{totalCount}', (totalCount || 0).toString())}
              </p>
            </div>
            
            {/* Boutons de tri complets - visibles uniquement sur desktop */}
            <div className="hidden sm:flex items-center gap-2 bg-white rounded-lg shadow-sm p-1">
              <button
                onClick={() => handleSortChange('rating')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  sortBy === 'rating'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                {t('public_decks.sort.best_rating')}
              </button>
              <button
                onClick={() => handleSortChange('votes')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  sortBy === 'votes'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Users className="w-4 h-4" />
                {t('public_decks.sort.most_votes')}
              </button>
              <button
                onClick={() => handleSortChange('recent')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  sortBy === 'recent'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Clock className="w-4 h-4" />
                {t('public_decks.sort.most_recent')}
              </button>
            </div>
          </div>

          {/* Barre de recherche et filtres - maintenant placée ici et avec la possibilité de la rétrécir */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-8 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 cursor-pointer"
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              <h2 className="text-lg font-semibold text-white flex items-center justify-between">
                <div className="flex items-center">
                  <Filter className="w-5 h-5 mr-2" />
                  {t('public_decks.filters.title')}
                </div>
                {isFilterExpanded ? (
                  <ChevronUp className="w-5 h-5 text-white" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white" />
                )}
              </h2>
            </div>
            
            {isFilterExpanded && (
              <div className="p-6">
                {/* Recherche par nom */}
                <div className="mb-6 relative">
                  <label htmlFor="search" className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Search className="w-5 h-5 mr-2 text-indigo-600" />
                    {t('public_decks.filters.deck_name')}
                  </label>
                  <div className="relative shadow-md rounded-lg hover:shadow-lg transition-shadow">
                    <input
                      type="text"
                      id="search"
                      className="block w-full rounded-lg border-2 border-indigo-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 sm:text-sm pr-24 py-3 pl-4 bg-white text-gray-800 placeholder-gray-400"
                      placeholder={t('public_decks.filters.placeholder')}
                      value={searchInputValue}
                      onChange={(e) => setSearchInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applySearchFilter();
                        }
                      }}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                      {searchInputValue && (
                        <button
                          type="button"
                          className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                          onClick={clearSearchInput}
                        >
                          <X className="h-5 w-5" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm hover:shadow-md"
                        onClick={applySearchFilter}
                      >
                        <Search className="h-4 w-4 mr-1.5" />
                        {t('public_decks.filters.search')}
                      </button>
                    </div>
                  </div>
                  {searchTerm && searchTerm !== searchInputValue && (
                    <div className="text-sm text-indigo-600 mt-2 flex items-center bg-indigo-50 p-2 rounded-md border border-indigo-100">
                      <Info className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>Filtre actuel: <span className="font-semibold">"{searchTerm}"</span></span>
                      <button 
                        className="ml-3 text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-1 text-xs rounded-md font-medium transition-colors"
                        onClick={applySearchFilter}
                      >
                        Mettre à jour
                      </button>
                    </div>
                  )}
                </div>

                {/* Filtre par type d'énergie */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Zap className="w-5 h-5 mr-2 text-indigo-600" />
                    {t('public_decks.filters.energy_type')}
                  </label>
                  <div className="relative shadow-md rounded-lg hover:shadow-lg transition-shadow">
                    <select
                      value={selectedEnergy || ''}
                      onChange={(e) => {
                        const newEnergy = e.target.value || null;
                        setSelectedEnergy(newEnergy);
                        
                        // Mettre à jour l'URL
                        const params = new URLSearchParams(searchParams);
                        if (newEnergy) {
                          params.set('energy', newEnergy);
                        } else {
                          params.delete('energy');
                        }
                        params.set('page', '1'); // Revenir à la première page
                        setSearchParams(params);
                        
                        // Réinitialiser la page
                        setCurrentPage(1);
                      }}
                      className="block w-full rounded-lg border-2 border-indigo-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 sm:text-sm py-3 pl-4 pr-10 bg-white text-gray-800"
                    >
                      <option value="">{t('public_decks.filters.all_energy_types')}</option>
                      {energyTypes.map((energy) => (
                        <option key={energy} value={energy}>
                          {translateEnergyType(energy, lang as 'fr' | 'en')}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedEnergy && (
                    <div className="mt-2 text-sm text-indigo-600 flex items-center bg-indigo-50 p-2 rounded-md border border-indigo-100">
                      <Info className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>
                        {selectedEnergy && 
                          t('public_decks.filters.decks_containing_type').replace('{{type}}', translateEnergyType(selectedEnergy, lang as 'fr' | 'en') || selectedEnergy)
                        }
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-indigo-600" />
                    {t('public_decks.filters.cards_in_deck')}
                  </h3>
                  
                  {/* Options de filtrage */}
                  <div className="mb-5 bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Mode de filtrage */}
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-800 mb-2 block">Mode de filtrage</label>
                        <div className="flex rounded-md overflow-hidden border-2 border-indigo-300 shadow-sm">
                          <button
                            type="button"
                            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                              cardFilterMode === 'any'
                                ? 'bg-indigo-600 text-white shadow-inner'
                                : 'bg-white text-gray-700 hover:bg-indigo-50'
                            }`}
                            onClick={() => changeCardFilterMode('any')}
                          >
                            {t('public_decks.filters.at_least_one_card')}
                          </button>
                          <button
                            type="button"
                            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                              cardFilterMode === 'all'
                                ? 'bg-indigo-600 text-white shadow-inner'
                                : 'bg-white text-gray-700 hover:bg-indigo-50'
                            }`}
                            onClick={() => changeCardFilterMode('all')}
                          >
                            {t('public_decks.filters.all_cards')}
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          {cardFilterMode === 'any' 
                            ? t('public_decks.filters.decks_containing_at_least_one_card')
                            : t('public_decks.filters.decks_containing_all_cards')}
                        </p>
                      </div>
                      
                      {/* Option pour les variantes */}
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-800 mb-2 block">Variantes de cartes</label>
                        <button
                          type="button"
                          onClick={toggleVariantsInclusion}
                          className={`w-full flex items-center justify-center gap-2 rounded-md border-2 px-3 py-2 text-sm font-medium shadow-sm transition-colors ${
                            includeVariants
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-inner'
                              : 'bg-white text-gray-700 border-indigo-300 hover:bg-indigo-50'
                          }`}
                        >
                          {includeVariants ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {t('public_decks.filters.include_variants')}
                            </>
                          ) : (
                            t('public_decks.filters.include_variants')
                          )}
                        </button>
                        <p className="text-xs text-gray-600 mt-2">
                          {includeVariants 
                            ? t('public_decks.filters.consider_different_versions')
                            : t('public_decks.filters.search_exact_cards')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recherche par cartes */}
                  <div className="mb-5">
                    <label htmlFor="card-search" className="block text-sm font-semibold text-gray-800 mb-2">
                      {t('public_decks.filters.search_cards')}
                    </label>
                    <div className="relative shadow-md rounded-lg hover:shadow-lg transition-shadow">
                      <input
                        type="text"
                        id="card-search"
                        className="block w-full rounded-lg border-2 border-indigo-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 sm:text-sm pl-12 pr-10 py-3 bg-white text-gray-800 placeholder-gray-400"
                        placeholder={t('public_decks.filters.placeholder_cards')}
                        value={cardSearchTerm}
                        onChange={(e) => {
                          setCardSearchTerm(e.target.value);
                          
                          // Démarrer la recherche si au moins 3 caractères sont saisis
                          if (e.target.value.trim().length >= 3) {
                            searchCards(e.target.value);
                          } else {
                            setSearchResults([]);
                          }
                        }}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      {isSearching && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <div className="h-5 w-5 border-t-2 border-indigo-600 rounded-full animate-spin"></div>
                        </div>
                      )}
                      {searchResults.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full bg-white shadow-xl rounded-lg max-h-72 overflow-auto border border-gray-200">
                          <div className="sticky top-0 bg-indigo-100 px-4 py-2.5 border-b border-indigo-200">
                            <p className="text-sm text-indigo-800 font-medium">
                              {t('public_decks.filters.results').replace('{{count}}', searchResults.length.toString())}
                            </p>
                          </div>
                          <ul className="py-2 divide-y divide-gray-100">
                            {searchResults.map((card) => (
                              <li
                                key={card.id}
                                className="py-2 px-2 hover:bg-indigo-50 transition-colors rounded-md"
                              >
                                <button
                                  className="w-full flex items-center gap-3"
                                  onClick={() => addCardToSelection(card)}
                                >
                                  <div className="w-12 h-12 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                                    <img
                                      src={getOptimizedImageUrl(card.image, IMAGE_SIZES.THUMBNAIL)}
                                      alt={card.card_fr || card.card_en || ''}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  </div>
                                  <div className="flex-grow text-left">
                                    <p className="text-sm font-medium truncate">{card.card_fr || card.card_en}</p>
                                    <p className="text-xs text-gray-500">{card.type || card.energy || ''}</p>
                                  </div>
                                  <button className="flex-shrink-0 text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 p-2 rounded-full transition-colors">
                                    <span className="sr-only">{t('public_decks.filters.add')}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-2 italic">
                      {t('public_decks.filters.add_cards_for_filtering')}
                    </p>
                  </div>
                
                  {/* Affichage des cartes sélectionnées */}
                  <div className={`rounded-lg ${selectedCards.length > 0 ? 'bg-indigo-50 p-4 border border-indigo-100' : ''}`}>
                    {selectedCards.length > 0 ? (
                      <>
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-sm font-medium text-indigo-800 flex items-center">
                            <Star className="w-4 h-4 mr-1" />
                            {t('public_decks.filters.selected_cards').replace('{{count}}', selectedCards.length.toString())}
                          </h4>
                          <button
                            type="button"
                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
                            onClick={() => {
                              setSelectedCardIds([]);
                              setSelectedCards([]);
                              
                              // Mettre à jour l'URL
                              const params = new URLSearchParams(searchParams);
                              params.delete('cards');
                              params.delete('card_filter_mode');
                              params.delete('include_variants');
                              params.set('page', '1');
                              setSearchParams(params);
                              setCurrentPage(1);
                            }}
                          >
                            <X className="h-3 w-3 mr-1" />
                            {t('public_decks.filters.clear_all')}
                          </button>
                        </div>
                        
                        {/* Mode de filtrage appliqué */}
                        <div className="mb-3 px-3 py-1.5 bg-white rounded-lg border border-indigo-100 text-xs text-indigo-800">
                          <span className="font-medium">{cardFilterMode === 'any' ? t('public_decks.filters.mode_at_least_one_card') : t('public_decks.filters.mode_all_cards')}</span>
                          {includeVariants && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                              </svg>
                              {t('public_decks.filters.variants_included')}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {selectedCards.map((card) => (
                            <div 
                              key={card.id} 
                              className="inline-flex items-center bg-white text-indigo-700 rounded-full px-3 py-1.5 text-sm font-medium shadow-sm border border-indigo-200 hover:bg-indigo-100 transition-colors"
                            >
                              <img 
                                src={getOptimizedImageUrl(card.image, IMAGE_SIZES.THUMBNAIL)}
                                alt={card.card_fr || card.card_en || ''}
                                className="h-6 w-auto mr-2" 
                                loading="lazy"
                              />
                              <span className="truncate max-w-[150px]">{card.card_fr}</span>
                              <button
                                type="button"
                                className="ml-1 text-indigo-500 hover:text-indigo-700"
                                onClick={() => removeCardFromSelection(card.id.toString())}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-sm text-gray-500 py-4">
                        {t('public_decks.filters.no_card_selected')}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions de filtrage */}
                <div className="border-t border-gray-200 mt-6 pt-6 flex items-center justify-between">
                  <div>
                    {(searchTerm || selectedCards.length > 0) && (
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        onClick={clearAllFilters}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t('public_decks.filters.reset_all_filters')}
                      </button>
                    )}
                  </div>
                  
                  <div className="inline-flex items-center gap-2">
                    <div className="inline-flex text-xs text-gray-500 items-center px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                      <Info className="h-4 w-4 mr-2 text-gray-400" />
                      {t('public_decks.filters.total_decks').replace('{{count}}', totalCount.toString())}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section des résultats de recherche - Decks */}
          <div className="w-full">
            {/* Total des résultats */}
            <div className="flex justify-between items-center mb-3 sm:mb-5">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                {t('public_decks.results.title')}
              </h2>
              
              {/* Tri des decks en version compacte pour mobile - visible uniquement sur mobile */}
              <div className="flex sm:hidden space-x-1">
                <button
                  onClick={() => handleSortChange('rating')}
                  className={`px-2 py-1 text-xs rounded-md transition-colors ${
                    sortBy === 'rating'
                      ? 'bg-indigo-600 text-white shadow-inner'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('public_decks.sort.best_rating')}
                </button>
                <button
                  onClick={() => handleSortChange('votes')}
                  className={`px-2 py-1 text-xs rounded-md transition-colors ${
                    sortBy === 'votes'
                      ? 'bg-indigo-600 text-white shadow-inner'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('public_decks.sort.most_votes')}
                </button>
                <button
                  onClick={() => handleSortChange('recent')}
                  className={`px-2 py-1 text-xs rounded-md transition-colors ${
                    sortBy === 'recent'
                      ? 'bg-indigo-600 text-white shadow-inner'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('public_decks.sort.most_recent')}
                </button>
              </div>
            </div>

            {/* Liste des decks en grille responsive */}
            {loading ? (
              <PublicDecksSkeleton />
            ) : (
              <>
                {decks.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                    {decks.map((deck, index) => (
                      <React.Fragment key={deck.id}>
                        <DeckListItem deck={deck} />
                        {/* Insérer la publicité après le 6ème deck */}
                        {index === 5 && (
                          <div className="col-span-1 sm:col-span-2 lg:col-span-3 2xl:col-span-4 bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 my-4 flex justify-center items-center">
                            <AdSense
                              adSlot="1424794127"
                              className="w-full min-h-[250px]"
                            />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <p className="text-gray-500 mb-2">{t('public_decks.results.no_deck_available')}</p>
                    <p className="text-gray-600">
                      {t('public_decks.results.try_modifying_criteria')} {' '}
                      <Link to={getPathForLang('/decks/my-decks')} className="text-indigo-600 hover:underline">
                        {t('public_decks.results.share_strategies')}
                      </Link>
                      !
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Pagination en version plus compacte pour mobile */}
            {totalPages > 1 && (
              <div className="mt-4 sm:mt-6 flex justify-center">
                <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 rounded-l-md text-xs sm:text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } border border-gray-300`}
                  >
                    <span className="sr-only">{t('public_decks.pagination.previous')}</span>
                    <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                  </button>
                  {renderPaginationButtons()}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 rounded-r-md text-xs sm:text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } border border-gray-300`}
                  >
                    <span className="sr-only">{t('public_decks.pagination.next')}</span>
                    <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            )}
          </div>

          {/* Annonce AdSense #3 - Avant la section FAQ */}
          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 mt-16 mb-16 flex justify-center items-center">
            <AdSense
              adSlot="1424794127"
              className="w-full min-h-[250px]"
            />
          </div>
          {/* Section FAQ */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('public_decks.faq.title')}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{t('public_decks.faq.how_to_create_deck')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('public_decks.faq.how_to_create_deck_description')}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{t('public_decks.faq.how_to_use_item_cards')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('public_decks.faq.how_to_use_item_cards_description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
}